import type { Tone } from '../../ui/v2';
import {
  parseOrderingParam,
  toggleSortState,
  toOrderingParam,
  type TableSortState,
} from '../../utils/listOrdering';
import {
  ADMIN_RESERVATION_FIELD_TO_SORT_KEY,
  ADMIN_RESERVATION_SORT_KEY_TO_FIELD,
  DEFAULT_ADMIN_RESERVATIONS_SORT,
  type AdminReservationSortKey,
} from './platformAdminOrdering';

export const ADMIN_RESERVATIONS_PAGE_SIZE = 20;
export const DEFAULT_ADMIN_RESERVATIONS_ORDERING = '-updated_at';
export const ADMIN_RESERVATIONS_SORT_ERROR = 'Could not apply sort.';

export type AdminReservation = {
  id?: string | number | null;
  store_name?: string | null;
  store_id?: string | number | null;
  updated_at?: string | null;
  start_time?: string | null;
  status?: string | null;
  origin_channel?: string | null;
  confirmation_code?: string | null;
  reservation_id?: string | number | null;
  customer_id?: string | number | null;
  party_size?: string | number | null;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
  guest_phone_number?: string | null;
  guest_email?: string | null;
  special_request?: string | null;
  deposit?:
    | number
    | string
    | { amount_dollars?: number | string | null }
    | null;
  table_type?: string | null;
};

/** Sort state from the `pa_res_ordering` URL value; unknown or absent → default. */
export const readAdminReservationsSort = (
  raw: string | null,
): TableSortState<AdminReservationSortKey> =>
  parseOrderingParam(raw, ADMIN_RESERVATION_FIELD_TO_SORT_KEY) ??
  DEFAULT_ADMIN_RESERVATIONS_SORT;

/** `ordering` sent to the API. */
export const adminReservationsOrdering = (
  sort: TableSortState<AdminReservationSortKey>,
): string =>
  toOrderingParam(sort, ADMIN_RESERVATION_SORT_KEY_TO_FIELD) ??
  DEFAULT_ADMIN_RESERVATIONS_ORDERING;

/**
 * Next `pa_res_ordering` URL value after clicking `key`: a new column starts
 * ascending, the active ascending column flips to descending, anything else
 * goes back to ascending. `null` removes the param.
 */
export const nextAdminReservationsOrderingParam = (
  raw: string | null,
  key: AdminReservationSortKey,
): string | null =>
  toOrderingParam(
    toggleSortState(readAdminReservationsSort(raw), key),
    ADMIN_RESERVATION_SORT_KEY_TO_FIELD,
  );

export const isAdminReservationSortable = (key: AdminReservationSortKey) =>
  Boolean(ADMIN_RESERVATION_SORT_KEY_TO_FIELD[key]);

/**
 * List state from a successful response. A paginated body replaces rows,
 * count and pages; a bare array counts itself on one page; anything else
 * clears the rows and leaves count and pages as they were (`undefined`).
 */
export const readAdminReservationsPage = (
  data: unknown,
): {
  reservations: AdminReservation[];
  count?: number;
  totalPages?: number;
} => {
  const body = data as {
    results?: unknown;
    count?: number;
    total_pages?: number;
  };
  if (body && body.results && Array.isArray(body.results)) {
    return {
      reservations: body.results,
      count: body.count || 0,
      totalPages:
        body.total_pages ||
        Math.ceil((body.count || 0) / ADMIN_RESERVATIONS_PAGE_SIZE),
    };
  }
  if (Array.isArray(data)) {
    return { reservations: data, count: data.length, totalPages: 1 };
  }
  return { reservations: [] };
};

/** OpenTable status words are free text; match on stems, first match wins. */
export const adminReservationStatusTone = (status?: string | null): Tone => {
  const s = (status || '').toLowerCase();
  if (s.includes('confirm')) return 'success';
  if (s.includes('cancel')) return 'danger';
  if (s.includes('pending') || s.includes('hold') || s.includes('wait')) {
    return 'warning';
  }
  if (s.includes('seat') || s.includes('arriv')) return 'accent';
  return 'neutral';
};

const formatAmount = (value: unknown) => {
  const n = Number(value);
  return !Number.isNaN(n)
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : String(value);
};

/** Deposit as a two-decimal amount; '' when there is nothing to show. */
export const formatAdminReservationDeposit = (
  deposit: AdminReservation['deposit'],
): string => {
  if (deposit == null || deposit === '') return '';
  if (typeof deposit === 'object') {
    return deposit.amount_dollars != null && deposit.amount_dollars !== ''
      ? formatAmount(deposit.amount_dollars)
      : '';
  }
  return formatAmount(deposit);
};

/** "Apr 26, 07:00 PM" in the browser's time zone; em dash when absent. */
export const formatAdminReservationDateTime = (v: string | null | undefined) =>
  v
    ? new Date(v).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export const adminReservationsPageLabel = (
  page: number,
  totalPages: number,
  count: number,
) => `Page ${page} of ${totalPages} (${count} reservations)`;
